import React, { useEffect, useState } from "react";
import { FolderArchive, Plus, Trash2, Download, Search, FileText, Upload } from "lucide-react";
import { PageHeader, SectionCard, Footer } from "@/components/common";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateID, todayISO } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const JENIS = ["SOP", "Sertifikat", "Izin", "JSA", "Kebijakan", "Laporan"];
const jenisColor = { SOP: "bg-blue-100 text-blue-700", Sertifikat: "bg-green-100 text-green-700", Izin: "bg-purple-100 text-purple-700", JSA: "bg-amber-100 text-amber-700", Kebijakan: "bg-slate-200 text-slate-700", Laporan: "bg-pink-100 text-pink-700" };

export default function DokumenAdministratif() {
  const { canEdit } = useAuth();
  const [name, setName] = useState("");
  const [jenis, setJenis] = useState("all");
  const [docs, setDocs] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", jenis: "SOP", expired_date: todayISO(), file: null, fileName: "" });
  const [delTarget, setDelTarget] = useState(null);

  const load = async () => {
    const params = {};
    if (name) params.name = name;
    if (jenis !== "all") params.jenis = jenis;
    const r = await api.get("/documents", { params });
    setDocs(r.data);
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [name, jenis]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setForm((p) => ({ ...p, file: reader.result, fileName: f.name }));
    reader.readAsDataURL(f);
  };
  const save = async () => {
    if (!form.name) { toast.error("Nama dokumen wajib"); return; }
    try { await api.post("/documents", { name: form.name, jenis: form.jenis, expired_date: form.expired_date, file: form.file }); setAddOpen(false); setForm({ name: "", jenis: "SOP", expired_date: todayISO(), file: null, fileName: "" }); toast.success("Dokumen ditambahkan"); load(); }
    catch (e) { toast.error("Gagal menyimpan"); }
  };
  const download = (d) => {
    if (!d.file) { toast.error("File belum diupload"); return; }
    const a = document.createElement("a"); a.href = d.file; a.download = `${d.name}.pdf`; a.click();
  };
  const del = async () => { const id = delTarget; setDelTarget(null); try { await api.delete(`/documents/${id}`); toast.success("Dokumen dihapus"); load(); } catch (e) { toast.error("Gagal"); } };

  return (
    <div className="p-6">
      <PageHeader icon={FolderArchive} title="DOKUMEN ADMINISTRATIF" subtitle="Kelola dokumen dan perizinan HSE PT. Ithikhara"
        right={canEdit && <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-9" onClick={() => setAddOpen(true)} data-testid="doc-add"><Plus className="w-4 h-4 mr-1" />Tambah Dokumen</Button>} />

      <SectionCard>
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative"><Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" /><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Cari nama dokumen" className="h-9 w-64 pl-8" data-testid="doc-search" /></div>
          <Select value={jenis} onValueChange={setJenis}><SelectTrigger className="h-9 w-44" data-testid="doc-filter-jenis"><SelectValue placeholder="Semua Jenis" /></SelectTrigger><SelectContent><SelectItem value="all">Semua Jenis</SelectItem>{JENIS.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="doc-table">
            <thead><tr className="text-left text-slate-500 border-b text-xs"><th className="py-2 px-3">No</th><th className="px-3">Nama Dokumen</th><th className="px-3">Jenis Dokumen</th><th className="px-3">Tanggal Expired</th><th className="px-3 text-center">File PDF</th><th className="px-3 text-center">Aksi</th></tr></thead>
            <tbody>
              {docs.map((d, i) => (
                <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-3">{i + 1}</td>
                  <td className="px-3 font-medium flex items-center gap-2"><FileText className="w-4 h-4 text-red-500" />{d.name}</td>
                  <td className="px-3"><span className={`text-xs px-2 py-0.5 rounded font-medium ${jenisColor[d.jenis] || "bg-slate-100 text-slate-600"}`}>{d.jenis}</span></td>
                  <td className="px-3 whitespace-nowrap">{formatDateID(d.expired_date)}</td>
                  <td className="px-3 text-center"><button onClick={() => download(d)} className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs" data-testid={`doc-download-${d.id}`}><Download className="w-4 h-4" />{d.file ? "Download" : "N/A"}</button></td>
                  <td className="px-3 text-center">{canEdit ? <button onClick={() => setDelTarget(d.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`doc-del-${d.id}`}><Trash2 className="w-4 h-4" /></button> : "-"}</td>
                </tr>
              ))}
              {docs.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Tidak ada dokumen</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-400 mt-3">Menampilkan {docs.length} dokumen</p>
      </SectionCard>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="doc-form"><DialogHeader><DialogTitle>Tambah Dokumen</DialogTitle><DialogDescription>Upload dokumen administratif (PDF).</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs">Nama Dokumen</Label><Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} data-testid="doc-name" /></div>
            <div><Label className="text-xs">Jenis Dokumen</Label><Select value={form.jenis} onValueChange={(v) => setForm((p) => ({ ...p, jenis: v }))}><SelectTrigger data-testid="doc-jenis"><SelectValue /></SelectTrigger><SelectContent>{JENIS.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Tanggal Expired</Label><Input type="date" value={form.expired_date} onChange={(e) => setForm((p) => ({ ...p, expired_date: e.target.value }))} data-testid="doc-expired" /></div>
            <div><Label className="text-xs">File PDF</Label>
              <label className="border-2 border-dashed border-slate-300 rounded-lg h-20 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 text-xs text-slate-500">
                <Upload className="w-5 h-5 mb-1" />{form.fileName || "Klik untuk upload PDF"}
                <input type="file" accept="application/pdf" className="hidden" onChange={onFile} data-testid="doc-file" />
              </label>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAddOpen(false)}>Batal</Button><Button className="bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="doc-save">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Hapus dokumen?</AlertDialogTitle><AlertDialogDescription>Dokumen akan dihapus permanen.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={del} data-testid="doc-del-confirm">Hapus</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
}
